import { useState, useEffect } from "react";
import { Box, Card, Grid, Stack, Typography } from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CircularLoader from "../../../../components/loaders/circularLoader";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import { useDispatch, useSelector } from "react-redux";
import { getLeadHistoryThunk } from "../../leadMangementThunk";
import { FieldSelect, FieldTextField } from "../../../../components/selections/fieldInputs";
import { QR, qrCardSx, QrActionButton } from "../../../qrCode/components/qrUi";
import moment from "moment"
import { updateLeadHistoryThunk, getMyTaskThunk } from "../../leadMangementThunk";

// Start icon per select field (presentation only)
const fieldIcons = {
    leadStatusType: <FlagOutlinedIcon />,
    callStatusType: <PhoneInTalkOutlinedIcon />,
    isFollowup: <EventRepeatOutlinedIcon />,
};

// Card section header: icon tile + title/subtitle (+ optional right content)
function SectionHeader({ icon, title, subtitle, color = QR.main, right }) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            sx={{
                position: "relative",
                overflow: "hidden",
                px: { xs: 2, sm: 2.5 },
                py: 1.75,
                borderBottom: `1px solid ${QR.border}`,
                bgcolor: "#fff",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, position: "relative", zIndex: 1 }}>
                <Box sx={{ width: 38, height: 38, borderRadius: 2.25, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: `linear-gradient(135deg, ${color}, ${color}b3)`, boxShadow: `0 6px 14px -8px ${color}` }}>
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>{title}</Typography>
                    {subtitle && <Typography sx={{ fontSize: 12.5, color: QR.slate }}>{subtitle}</Typography>}
                </Box>
            </Stack>
            <Box sx={{ position: "relative", zIndex: 1 }}>{right}</Box>
        </Stack>
    );
}

// Uppercase label above a field (same look as the field kit)
const kitLabelSx = { display: "block", mb: 0.9, fontSize: 13, fontWeight: 500, letterSpacing: 0.6, textTransform: "uppercase", color: "#4b5563" };

// Short placeholders so they are not cut off in the half-width card
const fieldPlaceholders = {
    leadStatusType: "Select status",
    callStatusType: "Select status",
};


export default function TaskUpdation({ show, handleClose, rowData, updationType }) {

    const dispatch = useDispatch();

    const { getLeadHistory = [], status1, status2, error, callStatusTypes = [], leadStatusTypes = [] } = useSelector(
        (state) => state.leadManagement
    );

    // Tinted status chip colours (cycled per history entry)
    const colors = [QR.blue, QR.purple, QR.main, QR.amber];

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [state, setState] = useState({
        leadStatusType: "",
        callStatusType: "",
        isFollowup: "",
        FollowUpDatetime: "",
        comments: "",
        errors: {},
    });

    const { leadStatusType, callStatusType, isFollowup, FollowUpDatetime, comments } = state;

    const updateFields = [
        { id: 1, name: "leadStatusType", value: leadStatusType, type: "select", label: "Lead Status", options: leadStatusTypes.map((item) => ({ value: item.id, label: item.lead_status_type })) },
        { id: 2, name: "callStatusType", value: callStatusType, type: "select", label: "Call Status", options: callStatusTypes.map((item) => ({ value: item.id, label: item.calling_status })) },
        { id: 3, name: "isFollowup", value: isFollowup, type: "select", label: "Is Followup", options: [{ value: 1, label: "Yes" }, { value: 2, label: "No" }] },
        ...isFollowup === 1 ? [{ id: 4, name: "FollowUpDatetime", value: FollowUpDatetime, type: "date", label: "FollowUp Datetime" }] : [],
        { id: 5, name: "comments", value: comments, type: "textArea", label: "Enter Comments" }
    ];

    useEffect(() => {
        if (show && rowData?.wa_contact_id) {
            dispatch(
                getLeadHistoryThunk({ credentials: { wa_contact_id: rowData?.wa_contact_id } })
            );


            setState((prev) => ({
                ...prev,
                leadStatusType: rowData?.lead_status_type_id ?? "-",
                callStatusType: rowData?.calling_status_id ?? "-"
            }))
        }
    }, [show, rowData, dispatch]);


    const handleSingleSelectChange = (name, selectedValue) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    useEffect(() => {
        if (status1 === "failed")
            setSnackbar({ open: true, message: error, severity: "error" });
    }, [status1, error]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validations = [
            { valid: !!leadStatusType, message: "Status Type is required", field: "leadStatusType" },
            { valid: !!callStatusType, message: "Call Status is required", field: "callStatusType" },
        ];

        for (let rule of validations) {
            if (!rule.valid) {
                setSnackbar({
                    open: true,
                    message: rule.message,
                    severity: "error",
                });

                setState((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, [rule.field]: rule.message },
                }));
                return;
            }
        }

        try {
            const result = await dispatch(
                updateLeadHistoryThunk({
                    credentials: {
                        "lead_status_type_id": leadStatusType,
                        "wa_contact_id": rowData?.wa_contact_id,
                        "calling_status_id": callStatusType,
                        "followup_time": FollowUpDatetime != "" ? moment(FollowUpDatetime).unix().toString() : "",
                        "comments": comments,
                    }
                })
            );

            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });

                if (updationType === "myTask") {
                    dispatch(getMyTaskThunk({ type: "getTask" }));
                }

                handleClose();
                setState(prev => ({
                    ...prev,
                    leadStatusType: "",
                    callStatusType: "",
                    isFollowup: "",
                    FollowUpDatetime: "",
                    comments: "",
                    errors: {},
                }))
            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    };

    return (
        <>
        <Grid container spacing={2} alignItems="stretch">
            {/* ---------- Lead update (left) ---------- */}
            <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ ...qrCardSx, height: "100%" }}>
                <SectionHeader
                    icon={<EditNoteOutlinedIcon sx={{ fontSize: 20 }} />}
                    title="Lead Update"
                    subtitle="Update the lead and call status, and plan a follow-up"
                />
                <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Grid container spacing={2.5}>
                        {updateFields.map((field) => (
                            <Grid key={field.id} size={field.type === "textArea" ? 12 : { xs: 12, sm: 6 }}>
                                {/* Is Followup: Yes / No toggle (same handler as the select) */}
                                {field.type === "select" && field.name === "isFollowup" && (
                                    <Box>
                                        <Typography component="span" sx={kitLabelSx}>{field.label}</Typography>
                                        <Stack direction="row" sx={{ p: 0.5, gap: 0.5, height: 42, boxSizing: "border-box", borderRadius: 2.5, bgcolor: "#f1f5f9", border: `1.5px solid ${state.errors[field.name] ? "#ef4444" : "#e5e7eb"}` }}>
                                            {field.options.map((opt) => {
                                                const active = field.value === opt.value;
                                                const c = opt.value === 1 ? QR.main : QR.slate;
                                                return (
                                                    <Box
                                                        key={opt.value}
                                                        component="button"
                                                        type="button"
                                                        onClick={() => handleSingleSelectChange(field.name, opt.value)}
                                                        sx={{
                                                            flex: 1,
                                                            border: 0,
                                                            cursor: "pointer",
                                                            borderRadius: 2,
                                                            fontFamily: "inherit",
                                                            fontSize: 13.5,
                                                            fontWeight: 700,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            gap: 0.5,
                                                            color: active ? "#fff" : "#64748b",
                                                            background: active ? `linear-gradient(135deg, ${c}, ${c}cc)` : "transparent",
                                                            boxShadow: active ? `0 4px 10px -6px ${c}` : "none",
                                                            transition: "all .15s ease",
                                                            "&:hover": { color: active ? "#fff" : "#0f172a", bgcolor: active ? undefined : "#fff" },
                                                        }}
                                                    >
                                                        {opt.value === 1 ? <EventRepeatOutlinedIcon sx={{ fontSize: 16 }} /> : <EventBusyOutlinedIcon sx={{ fontSize: 16 }} />}
                                                        {opt.label}
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                )}

                                {field.type === "select" && field.name !== "isFollowup" && (
                                    <FieldSelect
                                        name={field.name}
                                        label={field.label}
                                        options={field.options}
                                        value={field.value}
                                        onChange={handleSingleSelectChange}
                                        error={!!state.errors[field.name]}
                                        placeholder={fieldPlaceholders[field.name] || "Select"}
                                        icon={fieldIcons[field.name]}
                                    />
                                )}

                                {field.type === "date" && (
                                    <FieldTextField
                                        name={field.name}
                                        label={field.label}
                                        type="datetime-local"
                                        value={field.value}
                                        onChange={(e) =>
                                            setState((prev) => ({ ...prev, [field.name]: e.target.value }))
                                        }
                                    />
                                )}

                                {field.type === "textArea" && (
                                    <Box>
                                        <FieldTextField
                                            name={field.name}
                                            label={field.label}
                                            multiline
                                            rows={4}
                                            placeholder="Add notes about this call or follow-up"
                                            value={field.value}
                                            onChange={(e) =>
                                                setState((prev) => ({ ...prev, [field.name]: e.target.value }))
                                            }
                                        />
                                        <Typography sx={{ mt: 0.5, textAlign: "right", fontSize: 11.5, color: "#94a3b8" }}>
                                            {(field.value || "").length} characters
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        ))}
                    </Grid>

                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        sx={{ mt: 4, pt: 2.5, borderTop: `1px solid ${QR.border}` }}
                    >
                        <QrActionButton
                            variant="solid"
                            color={QR.main}
                            gradientTo={QR.light}
                            icon={<CheckCircleIcon sx={{ fontSize: 17 }} />}
                            label="Update"
                            loadingLabel="Updating.."
                            loading={status2 === "loading"}
                            onClick={handleSubmit}
                        />
                    </Stack>
                </Box>
            </Card>
            </Grid>

            {/* ---------- Lead history (right) ---------- */}
            <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ ...qrCardSx, height: "100%" }}>
                <SectionHeader
                    icon={<HistoryIcon sx={{ fontSize: 20 }} />}
                    title="Lead History"
                    subtitle="Previous updates for this contact"
                    color={QR.blue}
                    right={status1 === "succeeded" && getLeadHistory.length > 0 && (
                        <Box sx={{ px: 1.25, py: 0.25, borderRadius: 5, fontSize: 12.5, fontWeight: 700, color: QR.blue, bgcolor: `${QR.blue}14`, whiteSpace: "nowrap" }}>
                            {getLeadHistory.length} {getLeadHistory.length === 1 ? "entry" : "entries"}
                        </Box>
                    )}
                />

                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    {status1 === "loading" && (
                        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                            <CircularLoader color="success" />
                        </Box>
                    )}

                    {status1 === "succeeded" && (
                        getLeadHistory.length > 0 ? (
                            <Stack spacing={1.5} sx={{ maxHeight: { xs: 360, sm: 420 }, overflowY: "auto", overflowX: "hidden", pr: 0.5, pl: 0.25 }}>
                                {getLeadHistory.map((item, index) => {
                                    const chipColor = colors[index % colors.length];
                                    const isLast = index === getLeadHistory.length - 1;
                                    return (
                                        // Timeline row: dot + connecting line, then the entry card
                                        <Stack key={index} direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
                                        <Box sx={{ position: "relative", width: 22, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                                            <Box sx={{ mt: 1.75, width: 12, height: 12, borderRadius: "50%", bgcolor: "#fff", border: `3px solid ${chipColor}`, boxShadow: `0 0 0 4px ${chipColor}1f`, zIndex: 1 }} />
                                            {!isLast && <Box sx={{ position: "absolute", top: 30, bottom: -14, width: 2, borderRadius: 1, background: `linear-gradient(${chipColor}66, ${QR.border})` }} />}
                                        </Box>
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                p: { xs: 1.5, sm: 1.75 },
                                                borderRadius: 2.5,
                                                border: `1px solid ${QR.border}`,
                                                bgcolor: "#fff",
                                                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
                                                transition: "all .15s ease",
                                                "&:hover": { borderColor: `${chipColor}55`, boxShadow: `0 8px 18px -12px ${chipColor}` },
                                            }}
                                        >
                                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5} useFlexGap flexWrap="wrap">
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "#0f172a" }}>
                                                        <EventOutlinedIcon sx={{ fontSize: 16, color: QR.slate }} />
                                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                                                            {moment.unix(item.followup_time).format("DD MMM YYYY, h:mm a")}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5, color: QR.slate }}>
                                                        <PersonOutlineIcon sx={{ fontSize: 16 }} />
                                                        <Typography sx={{ fontSize: 12.5 }}>
                                                            Followed by <Box component="span" sx={{ fontWeight: 600, color: "#334155" }}>{item.employee_name}</Box>
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                                {item.calling_status && (
                                                    <Box sx={{ px: 1.25, py: 0.4, borderRadius: 5, fontSize: 12, fontWeight: 700, lineHeight: 1.5, color: chipColor, bgcolor: `${chipColor}14`, border: `1px solid ${chipColor}33`, whiteSpace: "nowrap" }}>
                                                        {item.calling_status}
                                                    </Box>
                                                )}
                                            </Stack>

                                            <Typography sx={{ mt: 1.25, pt: 1.25, borderTop: `1px dashed ${QR.border}`, fontSize: 13.5, color: item.comments ? "#334155" : "#94a3b8", whiteSpace: "pre-line", wordBreak: "break-word" }}>
                                                {item.comments || "No comments"}
                                            </Typography>
                                        </Box>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <Stack
                                alignItems="center"
                                spacing={1}
                                sx={{ py: 5, px: 2, textAlign: "center", borderRadius: 3, border: `1.5px dashed ${QR.border}`, background: `radial-gradient(circle at 50% 0%, ${QR.blue}0d 0, transparent 70%)` }}
                            >
                                <Box sx={{ position: "relative", width: 64, height: 64, mb: 0.5 }}>
                                    <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", bgcolor: `${QR.blue}0f`, border: `1px dashed ${QR.blue}40` }} />
                                    <Box sx={{ position: "absolute", inset: 10, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: `linear-gradient(135deg, ${QR.blue}, #60a5fa)`, boxShadow: `0 8px 18px -10px ${QR.blue}` }}>
                                        <HistoryIcon sx={{ fontSize: 22 }} />
                                    </Box>
                                </Box>
                                <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>No history yet</Typography>
                                <Typography sx={{ fontSize: 13, color: QR.slate, maxWidth: 260 }}>
                                    Each update you save on the left is added here as a timeline entry.
                                </Typography>
                                <Stack direction="row" spacing={0.75} sx={{ pt: 1 }}>
                                    {[QR.main, QR.blue, QR.purple].map((c) => (
                                        <Box key={c} sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: `${c}66` }} />
                                    ))}
                                </Stack>
                            </Stack>
                        )
                    )}
                </Box>
            </Card>
            </Grid>

        </Grid>
            <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />
        </>
    );
}
